import { format, isValid, parseISO } from 'date-fns'

import { parseUTCDateString } from '../../../utils/dates'

const YMD_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Parses category strings when the view marks the axis as time. Accepts:
 * - Full ISO-8601 datetimes (e.g. `2026-01-15T00:00:00.000Z`)
 * - Date-only day strings from cube (e.g. `2024-03-01`) as UTC midnight for stable sorting/labels
 */
export const parseChartCategoryDate = (raw: string): Date | undefined => {
  const s = String(raw).trim()
  if (!s) return undefined

  if (s.includes('T')) {
    return parseUTCDateString(s)
  }

  const ymd = YMD_ONLY.exec(s)
  if (ymd) {
    const d = parseISO(`${ymd[1]}-${ymd[2]}-${ymd[3]}T00:00:00.000Z`)
    return isValid(d) ? d : undefined
  }

  return undefined
}

/**
 * Sorts chart rows by parseable category dates (e.g. MoM line) when the axis is time-based.
 */
export const sortChartRowsByCategoryDate = <T extends Record<string, unknown>>(
  rows: T[],
  nameKey: string,
): T[] =>
  [...rows].sort((a, b) => {
    const sa = String(a[nameKey] ?? '')
    const sb = String(b[nameKey] ?? '')
    const da = parseChartCategoryDate(sa)?.getTime()
    const db = parseChartCategoryDate(sb)?.getTime()
    if (da != null && db != null) return da - db
    if (da != null) return -1
    if (db != null) return 1
    return sa.localeCompare(sb)
  })

/**
 * Axis tick lines: default is abbreviated month + year on two lines; custom [date-fns format](https://date.fns.org/v3/docs/format)
 * uses one line, or multiple lines when the formatted output contains a newline (e.g. `yyyy'\n'MM`).
 */
export const formatCategoryAxisTickLines = (
  date: Date,
  dateFormat?: string,
): string[] => {
  if (!dateFormat) {
    return [format(date, 'MMM'), format(date, 'yyyy')]
  }

  const out = format(date, dateFormat)
  if (out.includes('\n')) {
    return out.split('\n').filter(Boolean)
  }

  return [out]
}

/**
 * Single-line label for HTML tooltips (e.g. "Mar 2024"). Use when `categoryAxisIsDate` is true.
 */
export const formatCategoryTooltipLabel = (
  raw: string,
  dateFormat?: string,
): string => {
  const d = parseChartCategoryDate(raw)
  if (!d) return raw
  return format(d, dateFormat ?? 'MMM yyyy')
}
